import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    CONNECTION_TYPE,
    Device,
    getActualHeight,
    getActualWidth,
    getNetInfo,
    Orientation
} from 'DeviceUtils';
import { ConfirmationModal } from 'LocalGluons';
import { t } from 'Locale';
import { hasRelevantPackage } from 'Utils/userUtils';
import { getStreamOptionsByType, isVod } from 'Utils';
import appManager from 'AppManager';
import { DeviceStorageState } from 'Store/Store.types';
import { useStore } from 'Store';
import { PlayerData, StreamType } from 'Types';
import { getStreamTypeByItemType } from 'LocalGluons/components/PlayerContainer/playerUtils';
import { NativeEntitlementsResponse, StreamUrlResponse } from '@24i/st-api-connector';
import { Linking } from 'react-native';
import useMirroringCheck from 'LocalGluons/components/PlayerContainer/hooks/useMirroringCheck';
import MirroringPreventDialog from 'LocalGluons/components/PlayerContainer/components/MirroringPreventDialog';
import { EntitlementsModal } from 'LocalGluons/components/EntitlementsModal';
import DetailScene from './models/DetailScene';
import DetailModel from './models/DetailModel';
import SCREENS from '../../../context/NavigationHelper/Screens';
import { useNavigationPropsContext } from '../../Navigation/navigationPropsContext';

// TODO: refactor after 4.0 alpha merge
interface useDetailsLogicProps {
    item: unknown; // PlayerData["item"]
    navigation: {
        addListener: () => void;
        getParam: (param: string, fallback?: any) => unknown;
        navigate: (screen: any, params?: any) => void;
        replace: (screen: string, params: any) => void;
        getParent: () => void;
        push: (screen: any, params?: any) => void;
    };
    route: {
        params: {
            parentItem: unknown;
            updateSeriesDetail?: () => void;
            navigationPickerHeaderIsToggled: boolean | undefined;
            parent: undefined | string;
        };
    };
    mCoreConfig: unknown;
    updateItem: (item: any, forceUpdate?: boolean) => void;
    playerData: unknown; // PlayerData
    setCurrentScreen: (screen: any) => void;
    setModalContent: (modal: any) => void;
    deviceStorageState: DeviceStorageState;
    setPlayerData: (playerData: unknown) => void;
    loading: boolean;
    setLoading: (item: any) => void;
    propsItem: PlayerData['item'];
}

// eslint-disable-next-line max-statements
const useDetailsLogic = ({
    item,
    navigation,
    // route,
    mCoreConfig,
    updateItem,
    playerData,
    setCurrentScreen,
    setModalContent,
    deviceStorageState,
    setPlayerData,
    setLoading,
    propsItem
}: useDetailsLogicProps) => {
    // const { params } = route;
    const { navigationProps, navigate, updateNavigationProps } = useNavigationPropsContext();
    const model = new DetailModel(
        item || propsItem,
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        navigationProps.parentItem
    );
    const {
        seasons,
        episodes,
        parentItem,
        canBePlayed,
        hasRecommendedCarousel,
        item: modelItem,
        hasSeriesNavigationPicker,
        isArchiveAvailable,
        isDownloadable,
        hasTrailer
    } = model;
    const [playerInitialized, setPlayerInitialized] = useState(false);
    const scrollViewRef = useRef({ scrollTo: (position: number) => null });
    const [isUserAbleToPlay, setIsUserAbleToPlay] = useState(false);
    const { getIsMirroring } = useMirroringCheck();
    // TODO: Fix after store typing
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const { userData, setContinueWatchingEnabled, mySubscriptions } = useStore();

    const api = appManager.getApi();

    const scene = useMemo(() => new DetailScene(model, updateItem), []);
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const pickerIsDisplayed = navigationProps.navigationPickerHeaderIsToggled;

    // const isUserAbleToPlay = useMemo(
    //     () => (isVod(item) ? canBePlayed && hasRelevantPackage(item, userData) : canBePlayed),
    //     [canBePlayed, item, userData]
    // );

    useEffect(() => {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        if (item?.data?.channelType === 'VOD') {
            isDummySchedule();
        } else {
            setIsUserAbleToPlay(
                item && isVod(item) ? canBePlayed && hasRelevantPackage(item, userData) : canBePlayed
            );
        }
    }, [canBePlayed, item, userData]);

    const previewImage = model.getPreviewImage();
    const portraitHeight =
        Device.isPhone && getActualHeight() < getActualWidth()
            ? getActualWidth()
            : getActualHeight();
    const previewHeight = portraitHeight * 0.25;

    /**
     * Functions
     */
    const hideDialog = () => {
        setModalContent(null);
    };

    const onWifiDialogConfirm = () => {
        hideDialog();
        navigate('NetworkSettings', {});
    };

    const handleUpdateItem = (forceUpdate: boolean) => {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        if (navigationProps.updateSeriesDetail) navigationProps.updateSeriesDetail();
        updateItem(item, forceUpdate);
    };

    const handleDeepLink = () => {
        // @ts-ignore
        const deepLink = mCoreConfig.orderingServiceUrl;
        Linking.openURL(deepLink);
    };

    const onPickerSelect = (pickerItem) => {
        if (model.isEpisode || model.isRecording) {
            const selectedItem = parentItem.episodes[pickerItem.index];
            navigation.push('Details', {
                id: selectedItem.getId(),
                type: selectedItem.modelType,
                plaftorm: selectedItem.data?.pckg?.platform
            });
        } else if (model.isSeason) {
            const selectedItem = parentItem.seasons[pickerItem.index];
            navigation.push('Details', {
                id: selectedItem.getId(),
                type: selectedItem.modelType,
                plaftorm: selectedItem.data?.pckg?.platform
            });
        }
        updateNavigationProps({
            navigationPickerHeaderIsToggled: false,
            headerTitle: pickerItem.title
        });
    };

    const navigateToRelated = (newItem) => {
        navigation.push('Details', {
            id: newItem.getId(),
            type: newItem.modelType,
            plaftorm: newItem.data?.pckg?.platform
        });
    };

    const showNeedPackageModal = () =>
        setModalContent(
            <ConfirmationModal
                header={`${t('COMMON.INFO')}`}
                message={`${t('vod_service_not_granted')}`}
                confirmLabel={t('COMMON.OK')}
                onConfirm={hideDialog}
                onClose={hideDialog}
            />
        );

    const playContent = async (
        playerConfig: {
            item?: unknown;
            isTrailer?: boolean;
            checkEntitlements?: boolean;
        } = {}
    ) => {
        const isMirroring = await getIsMirroring();
        if (isMirroring) {
            setModalContent(<MirroringPreventDialog setModalContent={setModalContent} />);
        }
        if (!isMirroring) {
            const { itemToPlay } = model;
            setContinueWatchingEnabled(true);
            if (
                itemToPlay.modelType === 'recording' &&
                itemToPlay.getScheduleStatus() === 'archive'
            ) {
                const archiveItem = await api.fetchProgramDetail({
                    scheduleId: itemToPlay.data.id
                });

                if (archiveItem) {
                    setPlayerData({
                        ...playerConfig,
                        item: archiveItem
                    });
                    return;
                }
            }

            const itemFromModel =
                itemToPlay.episodes?.length > 0 ? itemToPlay.episodes[0] : itemToPlay;

            const newPlayerData = {
                ...playerConfig,
                item: playerConfig.item || itemFromModel
            };

            if (playerConfig.checkEntitlements) {
                const {
                    backstageConnector: { vod }
                } = appManager.getApi();
                let entitlementsResponse: NativeEntitlementsResponse;
                try {
                    const packageIds = [];
                    mySubscriptions.packages.forEach((subscriptionPackage) => {
                        subscriptionPackage.items.forEach((packageItem) => {
                            if (packageItem.type === 'VOD') {
                                packageIds.push(packageItem.id.toString());
                                // packageItem.id.toString();
                            }
                        });
                    });
                    entitlementsResponse = await vod.getMovieNativeEntitlements(
                        newPlayerData.item.getId(),
                        packageIds,
                        mySubscriptions.subscriberType
                    );
                } catch {
                    console.log(Error);
                }
                if (entitlementsResponse?.action === 'UPSELL_MESSAGE') {
                    setModalContent(
                        <EntitlementsModal
                            logo={
                                ''
                                // 'm_logo_entitlements'
                                // isUserFreemium(userData) ? 'magio_kino_logo' : 'magio_cinema_logo'
                            }
                            header={t(entitlementsResponse?.packageName)}
                            message={t(entitlementsResponse?.templateTextString)}
                            onCancel={hideDialog}
                            onClose={hideDialog}
                            confirmLabel={t('COMMON.FIND_MORE_INFO')}
                            webString={t('SS.VOD.ORDER_SUBS_INFO_WEB')}
                            storeString={t('SS.VOD.ORDER_SUBS_INFO_STORE')}
                            mobileString={t('SS.VOD.ORDER_SUBS_INFO_CALL')}
                            onConfirm={handleDeepLink}
                        />
                    );
                    return;
                }
            }

            if (Device.isWeb) {
                if (newPlayerData.item) setPlayerData(newPlayerData);
                return;
            }

            try {
                const netInfo = await getNetInfo();

                if (
                    netInfo.type === CONNECTION_TYPE.CELLULAR &&
                    !deviceStorageState.videoStreamingOverCellular
                ) {
                    setModalContent(
                        <ConfirmationModal
                            header={`${t('COMMON.WIFI_ONLY_IS_SET')}`}
                            message={t('COMMON.WIFI_ONLY_PLAY_DESCRIPTION')}
                            confirmLabel={t('COMMON.OPEN_NETWORK_SETTINGS')}
                            cancelLabel={t('COMMON.BTN_CANCEL')}
                            onConfirm={onWifiDialogConfirm}
                            onCancel={hideDialog}
                        />
                    );
                } else {
                    setPlayerData(newPlayerData);
                    setPlayerInitialized(true);
                }
            } catch (error) {
                console.log(error);
            }
        }
    };

    const onPlayPress = (isTrailer = false) => {
        if (model.canBePlayed) playContent({ isTrailer });
    };

    const onTrailerPress = () => {
        onPlayPress(true);
    };

    /**
     * Lifecycle
     */
    useEffect(() => {
        // Ensure portrait lock when coming from player or epg
        // Also make sure that you don't lock on portrait when player is opened
        // This can happen, detail can be updated "behind: player
        if (Device.isPhone && !playerData) {
            Orientation.lockToPortrait();
        }
    }, [navigation, playerData, scene, setCurrentScreen]);

    useEffect(
        () => {
            scene.setRefreshOnProgramEnd();
            setCurrentScreen(SCREENS.DETAIL);
            return () => {
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                if (navigationProps.parent === 'TvGuide' && Device.isPhone && !playerData) {
                    Orientation.unlockAllOrientations();
                }
                scene.removeRefreshOnProgramEnd();
            };
        },
        []
    );

    useEffect(() => {
        if (!playerData) {
            scene.setRefreshOnProgramEnd();
        } else {
            scene.removeRefreshOnProgramEnd();
        }
    }, [playerData]);

    useEffect(() => {
        // If player is closed and was opened from detail, update item for recordings data
        if (!playerData && playerInitialized) {
            setPlayerInitialized(false);
            updateItem(item);
        }

        if (pickerIsDisplayed) scrollViewRef.current.scrollTo(0);

        scene.updateData(model, updateItem);
    }, [playerData, playerInitialized, item, pickerIsDisplayed, model, updateItem, scene]);

    /**
     * Computed variables
     */
    const buttonsContainerProps = {
        style: Device.isTabletOrWeb ? { marginTop: 40 } : {},
        navigation,
        item: propsItem || item,
        parentItem,
        isArchiveAvailable,
        isDownloadable,
        onTrailerPress,
        onLoading: setLoading,
        updateItem: handleUpdateItem,
        setModalContent,
        hasTrailer
    };

    const isDummySchedule = useCallback(
        async (
            customStreamType?: StreamType
        ) => {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            const streamType = customStreamType || getStreamTypeByItemType(item);

            let wasSuccessful = true;
            const options = {
                ...(await getStreamOptionsByType(
                    item,
                    streamType,
                    deviceStorageState.videoQualityProfile
                ))
            };
            try {
                const isMirroring = await getIsMirroring();
                if (!isMirroring) {
                    const streamData: StreamUrlResponse =
                        await appManager.api.fetchStreamUrl(options);
                } else {
                    setModalContent(<MirroringPreventDialog setModalContent={setModalContent} />);
                }
            } catch (err) {
                const error = err as { errorCode: string };
                if (error.errorCode === 'DUMMY_SCHEDULE') {
                    wasSuccessful = false;
                }
            }
            if (isVod(item)) {
                if (canBePlayed && hasRelevantPackage(item, userData)) {
                    setIsUserAbleToPlay(true);
                } else {
                    setIsUserAbleToPlay(false);
                }
            } else if (canBePlayed) {
                setIsUserAbleToPlay(wasSuccessful);
            } else {
                setIsUserAbleToPlay(false);
            }
        },
        [appManager.api, item]
    );

    // useEffect(() => { console.log('AHOJ= item v useDetailLogic ', item) }, [item]);
    return {
        hasSeriesNavigationPicker,
        scrollViewRef,
        pickerIsDisplayed,
        modelItem,
        hasRecommendedCarousel,
        parentItem,
        isUserAbleToPlay,
        buttonsContainerProps,
        seasons,
        episodes,
        previewImage,
        previewHeight,
        handleUpdateItem,
        onPickerSelect,
        navigateToRelated,
        playContent
    };
};

export default useDetailsLogic;
